/**
 * Patient consumption → IPD pharmacy charge capture helpers.
 * Called after POST .../dispensing/consumption/{id}/confirm/ for PATIENT only.
 */
import { getConsumption, getConsumptionItems } from '../api/dispensing';
import { getItem } from '../api/inventoryMaster';
import { toInventoryList } from '../api/inventory-request';
import { postPharmacyChargeCapture } from '../../ipd-billing/billing-api';

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function toChargeDecimal(value) {
  const n = toNumber(value);
  return (n == null ? 0 : n).toFixed(4);
}

function isPatientConsumption(row) {
  return row?.consumption_type === 'PATIENT';
}

/**
 * Group line items by item_id for
 * POST ipd_admission/{id}/billing/pharmacy-charge-capture/
 */
/**
 * The consumption-items API returns the medicine name under varied keys
 * (item_name / medicine_name / product_name / drug_name / name, sometimes on a
 * nested item object). The pharmacy charge-capture API requires a non-empty
 * item_name, so resolve it from any of those shapes (mirrors BillDetailDrawer).
 */
function resolveItemName(line) {
  const nested = line.item || line.medicine || line.product || {};
  const name =
    line.item_name || line.medicine_name || line.product_name || line.drug_name || line.name ||
    nested.item_name || nested.medicine_name || nested.product_name || nested.name ||
    line.item_code || '';
  return String(name).trim();
}

export function buildPharmacyChargePayloadFromItems(itemLines) {
  const byItem = new Map();
  (itemLines || []).forEach((line) => {
    const itemId = line.item_id || line.itemId;
    if (!itemId) return;
    let entry = byItem.get(itemId);
    if (!entry) {
      entry = {
        item_id: itemId,
        item_name: resolveItemName(line),
        batches: [],
      };
      // Inventory scope so the billing package engine can fold this into a pharmacy /
      // inventory bucket (e.g. an ALL_PHARMACY entitlement matches on is_drug_category /
      // item_type=="medicine"). Sourced from the item master via enrichItems().
      const itemType = line.item_type;
      const isDrug = line.is_drug_category ?? line.is_drug;
      const invItemCode = line.inventory_item_code ?? line.item_code;
      const invCatCode = line.inventory_category_code ?? line.category_code;
      if (itemType != null && itemType !== '') entry.item_type = itemType;
      if (isDrug != null) entry.is_drug_category = Boolean(isDrug);
      if (invItemCode) entry.inventory_item_code = String(invItemCode);
      if (invCatCode) entry.inventory_category_code = String(invCatCode);
      byItem.set(itemId, entry);
    }
    const qty = line.requested_quantity ?? line.quantity ?? line.confirmed_quantity;
    const rate = toNumber(line.sale_price)
      || toNumber(line.salePrice)
      || toNumber(line.unit_rate)
      || toNumber(line.unitRate)
      || toNumber(line.mrp);
    const batch = {
      quantity: toChargeDecimal(qty),
      batch_number: line.batch_number || line.batchNumber || '',
      unit_rate: toChargeDecimal(rate),
    };
    const expiry = line.expiry_date || line.expiryDate;
    if (expiry) batch.expiry_date = expiry;
    entry.batches.push(batch);
  });
  return { items: Array.from(byItem.values()) };
}

/**
 * The consumption-items API returns only `item_id` — no name AND no inventory
 * classification. Look each item up in the item master to fill (a) the item_name the
 * charge-capture API requires, and (b) the inventory scope (item_type / is_drug /
 * item_code / category_code) the billing package engine needs to fold the charge into
 * a pharmacy/inventory bucket. Without the scope, an ALL_PHARMACY package entitlement
 * never matches and pharmacy items fall out of the package.
 */
async function enrichItems(itemLines) {
  const ids = [
    ...new Set((itemLines || []).map((l) => l.item_id || l.itemId).filter(Boolean)),
  ];
  if (!ids.length) return itemLines;

  const byId = {};
  await Promise.all(
    ids.map(async (id) => {
      try {
        const item = await getItem(id);
        byId[id] = item?.data ?? item ?? {};
      } catch (e) {
        /* leave unresolved — name/scope just stay whatever the line already had */
      }
    }),
  );

  return itemLines.map((l) => {
    const id = l.item_id || l.itemId;
    const obj = id ? byId[id] : null;
    if (!obj) return l;
    const merged = { ...l };
    if (!resolveItemName(l)) {
      merged.item_name = obj.item_name || obj.item_short_name || obj.brand_name || obj.item_code || '';
    }
    // Only fill scope the line doesn't already carry (line values win).
    if (merged.item_type == null && obj.item_type != null) merged.item_type = obj.item_type;
    if (merged.is_drug_category == null && obj.is_drug != null) merged.is_drug_category = Boolean(obj.is_drug);
    if (merged.inventory_item_code == null && obj.item_code) merged.inventory_item_code = obj.item_code;
    if (merged.inventory_category_code == null && obj.category_code) merged.inventory_category_code = obj.category_code;
    return merged;
  });
}

/**
 * After successful confirm of a PATIENT consumption, post pharmacy charges.
 * Throws if charge capture fails (caller may warn-and-continue).
 * No-ops for non-patient consumptions.
 */
export async function captureChargesAfterPatientConfirm(confirmTarget) {
  if (!isPatientConsumption(confirmTarget)) return { skipped: true, reason: 'not_patient' };

  const id = confirmTarget.transaction_id || confirmTarget.id;
  let admissionId = confirmTarget.admission_id || confirmTarget.admissionId || null;
  let itemLines = Array.isArray(confirmTarget.items) ? confirmTarget.items : null;

  if (!admissionId || !itemLines?.length) {
    // API: GET /inventory/dispensing/consumption/{id}/
    const detail = await getConsumption(id);
    admissionId = admissionId
      || detail?.admission_id
      || detail?.admissionId
      || null;
    if (!itemLines?.length) {
      const embedded = detail?.items || detail?.lines;
      itemLines = Array.isArray(embedded) ? embedded : null;
    }
  }

  if (!itemLines?.length) {
    // API: GET /inventory/dispensing/consumption/{id}/items/
    itemLines = toInventoryList(await getConsumptionItems(id));
  }

  if (!admissionId) {
    const err = new Error('Missing admission for pharmacy charge capture');
    err.code = 'MISSING_ADMISSION';
    throw err;
  }
  if (!itemLines?.length) {
    const err = new Error('No consumption items for pharmacy charge capture');
    err.code = 'MISSING_ITEMS';
    throw err;
  }

  // Consumption-items carry only item_id — resolve name + inventory scope from the item master.
  itemLines = await enrichItems(itemLines);

  // API: POST /app/api/ipd_admission/{id}/billing/pharmacy-charge-capture/
  await postPharmacyChargeCapture(admissionId, buildPharmacyChargePayloadFromItems(itemLines));
  return { skipped: false };
}
